import * as React from 'react';
import { z } from 'zod';
import { FormLayout } from './Field';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Input, Textarea } from './Input';
import { Stack } from './Layout';
import { Select, type SelectOption } from './Select';

export const SALE_PAYMENT_METHODS = [
  'Cash',
  'Card',
  'Check',
  'Bank transfer',
  'Other',
];

/**
 * The sale fields as a schema fragment, for a dialog to spread into its own:
 *
 *   z.object({ ...saleRecordShape, lienNumber: … })
 *
 * The price is a number, not a coerced one: `z.coerce.number()` turns the
 * empty string into 0, which would make an empty price box acceptable and
 * drop its required marker. The input reports '' for empty instead, which
 * fails the type and reads as "Enter a sale price."
 */
export const saleRecordShape = {
  salePrice: z
    .number({
      required_error: 'Enter a sale price.',
      invalid_type_error: 'Enter a sale price.',
    })
    .min(0, 'A sale price cannot be negative.'),
  /** As a date input holds it — YYYY-MM-DD. */
  saleDate: z.string().min(1, 'Enter the sale date.'),
  buyerName: z.string().trim().min(1, "Enter the buyer's name."),
  buyerPhone: z.string(),
  paymentMethod: z.string(),
  saleNotes: z.string(),
};

export const saleRecordSchema = z.object(saleRecordShape);

/** Every value as the form holds it. */
export type SaleRecordValues = z.infer<typeof saleRecordSchema>;

export interface SaleRecordFieldsProps<S extends FormSchema> {
  /** The dialog's form; its schema spreads `saleRecordShape`. */
  form: ZodForm<S>;
  /** A muted line under the price — "Balance owed on this impound: $450.00". */
  priceHint?: React.ReactNode;
  paymentMethods?: string[];
  notesPlaceholder?: string;
}

/**
 * The buyer and sale capture: price and date, buyer name and phone, payment
 * method, notes. Used by both places a vehicle is recorded as sold.
 *
 *   <SaleRecordFields form={form} />
 *
 * The ids the fields used to take a prefix for are generated now, so two of
 * these on one page no longer collide by construction.
 */
export function SaleRecordFields<S extends FormSchema>({
  form,
  priceHint,
  paymentMethods = SALE_PAYMENT_METHODS,
  notesPlaceholder = 'Lien sale certificate handed over; buyer arranging transport Friday.',
}: SaleRecordFieldsProps<S>) {
  /*
   * The caller's schema spreads `saleRecordShape`, so these six names are on
   * it — but a wider object is not assignable to the narrower one, and
   * TypeScript cannot see the spread from here. The cast says what the prop's
   * doc comment already requires.
   */
  const f = form as unknown as ZodForm<typeof saleRecordSchema>;
  const methodOptions: SelectOption[] = paymentMethods.map((method) => ({
    value: method,
    label: method,
  }));

  const priceField = (
    <FormField form={f} name="salePrice" label="Sale price (USD)">
      {(field) => (
        <Input
          type="number"
          min={0}
          name={field.name}
          ref={field.ref}
          onBlur={field.onBlur}
          value={field.value ?? ''}
          onChange={(e) =>
            field.onChange(e.target.value === '' ? '' : Number(e.target.value))
          }
        />
      )}
    </FormField>
  );

  return (
    <Stack gap={4}>
      <FormLayout columns={2} gap="comfortable">
        {priceHint ? (
          <Stack gap={2}>
            {priceField}
            {priceHint}
          </Stack>
        ) : (
          priceField
        )}
        <FormField form={f} name="saleDate" label="Sale date">
          {(field) => <Input type="date" {...field} />}
        </FormField>
      </FormLayout>

      <FormLayout columns={2} gap="comfortable">
        <FormField form={f} name="buyerName" label="Buyer name">
          {(field) => <Input {...field} placeholder="John Doe" />}
        </FormField>
        <FormField form={f} name="buyerPhone" label="Buyer phone">
          {(field) => (
            <Input type="tel" {...field} placeholder="(408) 555-0134" />
          )}
        </FormField>
      </FormLayout>

      <FormField form={f} name="paymentMethod" label="Payment method">
        {(field) => (
          <Select
            options={methodOptions}
            value={field.value}
            onValueChange={field.onChange}
          />
        )}
      </FormField>

      <FormField form={f} name="saleNotes" label="Notes">
        {(field) => (
          <Textarea {...field} rows={2} placeholder={notesPlaceholder} />
        )}
      </FormField>
    </Stack>
  );
}
